<template>
  <div
    id="station-registration-section"
    data-testid="station-registration-section"
    v-if="canReadDoctorCheck && hasStationStarted"
    class="station-registration-card flex flex-col rounded-lg px-3 py-3 md:px-4"
  >
    <!-- Nasce sempre aberto: o registro de presença é a informação que o gestor
         mais consulta no detalhe, e recolhê-lo (mesmo com pós-plantão avaliado)
         fazia parecer que o plantão não tinha registro. -->
    <CollapsibleSection
      title="Registro de plantão"
      test-id="duty-log-accordion"
      :touch-target="touchTarget"
      expand-label="Ver registros de plantão"
      collapse-label="Ocultar registros de plantão"
    >
      <template #title-prefix>
        <SVGIcon
          v-if="exceptionSummary"
          :path="mdiLockOpenVariant"
          class="inline-block w-4 h-4 text-negative align-text-bottom mr-1"
        />
      </template>

      <!-- ==================================================================
           Estado de erro (M1, balão ⑥): o cálculo do efetivo falhou. A seção
           mostra o motivo e o retry — NUNCA derruba o modal, que continua
           servindo valor, pós-plantão, observações e tudo mais.
           ================================================================== -->
      <div
        v-if="loadError"
        class="flex flex-col items-start gap-2 py-3 text-left"
        data-testid="station-registration-error"
      >
        <span class="text-sm text-negative"
          >Não foi possível calcular o horário efetivo deste plantão.</span
        >
        <span class="text-xs text-gray-500">{{ loadError }}</span>
        <button
          type="button"
          class="text-sm font-semibold text-primary30 underline"
          data-testid="station-registration-retry"
          @click="emit('retryRegistration')"
        >
          Tentar novamente
        </button>
      </div>

      <!-- ==================================================================
           Render POR PERNA (F2). Só existe quando o backend serve
           `GET /stations/:id/registration`; sem ele a seção cai no render de
           hoje, logo abaixo.
           ================================================================== -->
      <template v-else-if="registration">
        <div class="flex flex-wrap items-center gap-2 pb-3">
          <LabelBadge
            :label="statusLabel"
            :color="statusColor"
            size="small"
            :icon="null"
            data-testid="station-registration-status-chip"
          />
          <!-- O selo "Fechado" fica AO LADO do chip, nunca no lugar dele: o
               gestor precisa continuar vendo se o registro está OK ou pendente. -->
          <LabelBadge
            v-if="isFrozen"
            label="Fechado"
            color="gray"
            size="small"
            :icon="null"
            data-testid="station-registration-frozen-chip"
          />
        </div>

        <!-- Tela 4 do M1: o congelamento precisa dizer o que ainda é aceito, ou o
             gestor lê "Fechado" como "não envie mais nada". O motivo vem pronto do
             backend (`frozen.reason`); sem ele o texto genérico continua correto. -->
        <p
          v-if="isFrozen"
          class="m-0 mb-3 rounded border border-gray-30 bg-gray-50 px-2 py-1.5 text-left text-xs text-gray-600"
          data-testid="station-registration-frozen-banner"
        >
          {{ frozenText }}
        </p>

        <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div
            v-for="leg in legViews"
            :key="leg.key"
            class="flex flex-col gap-2 rounded border border-gray-30 p-3 text-left"
            :data-testid="`station-registration-leg-${leg.key}`"
          >
            <div class="flex flex-wrap items-center justify-between gap-2">
              <span class="text-xs font-bold uppercase tracking-wide text-gray-500">{{
                leg.title
              }}</span>
              <span class="flex items-center gap-1">
                <LabelBadge
                  v-if="isFrozen"
                  label="congelado"
                  color="gray"
                  size="small"
                  :icon="null"
                  :data-testid="`station-registration-leg-frozen-${leg.key}`"
                />
                <!-- SELO INERTE CONTRA O BACKEND F4, de propósito.
                     `GET /stations/:id/registration` NÃO serve verificação (T6
                     declinou o `verified`): o normalizador da rede crava
                     `verifiedAt: null`, então este `v-if` nunca é verdadeiro
                     hoje. Fica no template porque a regra existe e mora em
                     `GET /doctor-checks/:checkId/verification` — o `refId` da
                     perna (em `leg.origin`) é o endereço para chamá-la. Nenhum
                     teste espera este selo; ligá-lo é preencher `verifiedAt`.
                     A Tela 1 do mockup mostra "Verificado" no check-in e hoje
                     ele não tem de onde vir (ponta aberta com o produto). -->
                <LabelBadge
                  v-if="leg.verified"
                  label="Verificado"
                  color="green"
                  size="small"
                  :icon="null"
                />
              </span>
            </div>

            <div class="flex flex-wrap items-baseline gap-2">
              <span class="text-2xl font-bold leading-none">{{ leg.time }}</span>
              <LabelBadge
                v-if="leg.declared"
                label="declarado"
                color="yellow"
                size="small"
                :icon="null"
                :data-testid="`station-registration-leg-declared-${leg.key}`"
              />
              <span class="text-xs text-gray-500">{{ leg.timeDetail }}</span>
            </div>

            <div class="flex flex-col gap-1 text-xs">
              <span v-if="leg.sourceLabel"
                >Fonte: <span class="font-semibold">{{ leg.sourceLabel }}</span></span
              >
              <span v-else class="text-gray-500">{{ leg.emptyLabel }}</span>
              <span v-if="leg.declared" class="text-gray-500"
                >Horário declarado — não entra no efetivo até a aprovação.</span
              >
            </div>

            <!-- O ranking fica sozinho na linha desde que os dois "ver origem" por
                 perna deram lugar ao "Ver evidências" único do rodapé: origem e
                 evidência eram a mesma pergunta feita em dois lugares. -->
            <div v-if="leg.rankLabel" class="flex flex-wrap items-center justify-end gap-2">
              <span class="text-xs text-gray-500">{{ leg.rankLabel }}</span>
            </div>

            <StationSourceHistoryAccordion
              :sources="leg.supersededSources"
              :test-id="`station-source-history-accordion-${leg.key}`"
              :timezone="timezone"
            />
          </div>
        </div>

        <!-- Uma linha para as duas ações do rodapé. O wrapper do CTA continua
             existindo com o mesmo testid e o mesmo gate: o gate de envio (I9) é
             regra de negócio, não de layout. -->
        <div
          v-if="(hasLegWithoutSource && canSendRecord) || hasAnyOrigin"
          class="flex flex-wrap items-center justify-start gap-2 pt-3"
          data-testid="station-registration-actions"
        >
          <!-- Mesmo gate do botão do menu flutuante (I9): sem permissão de emissão
               ou com a escala recusando Registro de Atividade o clique morreria no
               `openSendRecordModal`, que volta cedo. -->
          <div v-if="hasLegWithoutSource && canSendRecord" data-testid="station-registration-cta">
            <button
              type="button"
              class="rounded border border-solid border-primary30 px-3 py-1.5 text-sm font-semibold text-primary30 hover:bg-primary5"
              data-testid="station-registration-send-record"
              @click="emit('sendRecord')"
            >
              Enviar registro
            </button>
          </div>

          <!-- Uma porta só para as evidências das duas pernas. Some quando nenhuma
               perna tem fonte: abrir um modal com dois estados vazios é pior do que
               não ter o botão. -->
          <button
            v-if="hasAnyOrigin"
            type="button"
            class="rounded border border-solid border-primary30 px-3 py-1.5 text-sm font-semibold text-primary30 hover:bg-primary5"
            data-testid="station-registration-view-evidence-button"
            @click="emitViewEvidence()"
          >
            Ver evidências
          </button>
        </div>

        <div
          class="mt-3 border-t border-gray-30 pt-2 text-left text-xs text-gray-500"
          data-testid="station-registration-footer"
        >
          {{ footerText }}
        </div>
      </template>

      <!-- ==================================================================
           Render de HOJE. Continua sendo o caminho padrão enquanto o
           `GET /stations/:id/registration` não existir em produção.
           ================================================================== -->
      <template v-else>
        <div
          class="station-grid-container flex flex-wrap gap-3 md:gap-4 py-2 px-1 md:px-2"
          v-if="showDoctorCheck"
        >
          <div class="station-block station-block--check flex flex-col gap-2 md:gap-3 min-w-0">
            <span class="font-bold text-sm md:text-base whitespace-nowrap">Check-in</span>
            <div class="flex flex-row flex-wrap gap-2 min-w-0">
              <div
                class="station-field flex flex-row items-center rounded border border-gray-30 gap-1 md:gap-2 py-1.5 md:py-2 px-2 min-w-0"
              >
                <Calendar :width="16" :height="16" />
                <span class="text-sm md:text-base whitespace-nowrap">{{
                  checkin?.date ? checkin?.date : 'Não registrado'
                }}</span>
              </div>
              <div
                class="station-field station-field--time flex flex-row flex-wrap items-center rounded border border-gray-30 gap-1 md:gap-2 py-1.5 md:py-2 px-2 min-w-0"
              >
                <Clock />
                <span class="text-sm md:text-base whitespace-nowrap">{{
                  checkin?.time ? checkin?.time : '--:--'
                }}</span>
                <LabelBadge
                  v-if="permanenceVisibility.showCheckinChip"
                  label="🔗 PERMANÊNCIA"
                  color="blue"
                  size="small"
                  :icon="null"
                  data-testid="permanence-checkin-chip"
                />
              </div>
            </div>
          </div>
          <div class="station-block station-block--check flex flex-col gap-2 md:gap-3 min-w-0">
            <span class="font-bold text-sm md:text-base whitespace-nowrap">Check-out</span>
            <div class="flex flex-row flex-wrap gap-2 min-w-0">
              <div
                class="station-field flex flex-row items-center rounded border border-gray-30 gap-1 md:gap-2 py-1.5 md:py-2 px-2 min-w-0"
              >
                <Calendar :width="16" :height="16" />
                <span class="text-sm md:text-base whitespace-nowrap">{{
                  checkout?.date ? checkout?.date : missingCheckoutLabel
                }}</span>
              </div>
              <div
                class="station-field station-field--time flex flex-row flex-wrap items-center rounded border border-gray-30 gap-1 md:gap-2 py-1.5 md:py-2 px-2 min-w-0"
              >
                <Clock />
                <span class="text-sm md:text-base whitespace-nowrap">{{
                  checkout?.time ? checkout?.time : '--:--'
                }}</span>
                <LabelBadge
                  v-if="permanenceVisibility.showCheckoutChip"
                  label="🔗 PERMANÊNCIA"
                  color="blue"
                  size="small"
                  :icon="null"
                  data-testid="permanence-checkout-chip"
                />
              </div>
            </div>
          </div>
          <div class="station-block station-block--origin flex flex-col gap-2 md:gap-3 min-w-0">
            <span class="font-bold text-sm md:text-base whitespace-nowrap">Registro</span>
            <div
              class="flex flex-row items-center rounded border border-gray-30 gap-1 md:gap-2 py-1.5 md:py-2 px-2 w-full min-w-0"
            >
              <span class="text-sm md:text-base break-words">{{
                mainCheck?.originLabel || 'Não identificado'
              }}</span>
            </div>
          </div>
          <div
            class="station-block station-block--verification flex flex-col gap-2 md:gap-3 min-w-0"
          >
            <span class="font-bold text-sm md:text-base whitespace-nowrap">{{
              VERIFICATION_COLUMN_HEADER
            }}</span>
            <div
              class="flex flex-row items-center rounded border border-gray-30 gap-1 md:gap-2 py-1.5 md:py-2 px-2 w-full min-w-0"
            >
              <v-btn
                v-if="verificationState !== 'NONE'"
                :color="verificationState === 'BIOMETRY' ? '#6B4FA3' : '#2F6F9F'"
                depressed
                class="verification-btn station-action-btn w-full !text-white"
                data-testid="verification-button"
                @click="emit('openVerification', mainCheck!.id)"
              >
                {{ VERIFICATION_BUTTON_LABELS[verificationState] }}
              </v-btn>
              <span
                v-else
                class="text-sm md:text-base text-gray-500"
                data-testid="verification-none"
                >{{ VERIFICATION_BUTTON_LABELS.NONE }}</span
              >
            </div>
          </div>
          <div class="station-block station-block--evidence flex flex-col gap-2 md:gap-3 min-w-0">
            <span class="font-bold text-sm md:text-base whitespace-nowrap">Evidência</span>
            <div
              class="flex flex-row items-center rounded border border-gray-30 gap-1 md:gap-2 py-1.5 md:py-2 px-2 w-full min-w-0"
            >
              <v-btn
                v-if="mainCheck?.checkEvidences?.length"
                color="primary"
                depressed
                class="station-action-btn w-full"
                @click="openEvidence(mainCheck.checkEvidences)"
              >
                {{ mainCheck.checkEvidences.length }} Arquivo{{
                  mainCheck.checkEvidences.length > 1 ? 's' : ''
                }}
              </v-btn>
              <v-btn
                v-else-if="isConciliationOrigin && attendanceStatus.containerId"
                color="primary"
                depressed
                class="station-action-btn w-full"
                :loading="loadingDsEvidence"
                @click="openDsAttendanceEvidence"
              >
                Visualizar
              </v-btn>
              <span v-else class="text-sm md:text-base text-gray-500">Sem evidência</span>
            </div>
          </div>
        </div>
        <div v-else class="station-empty-state flex justify-center items-center py-3 px-2">
          <span class="text-gray-500">Este plantão não possui registro de presença</span>
        </div>
      </template>

      <PermanenceLinkRow
        v-for="link in permanenceVisibility.links"
        :key="`${link.direction}-${link.recordId}`"
        :direction="link.direction"
        :station-label="link.stationLabel"
        :doctor-station-id="link.doctorStationId"
        :station-id="link.stationId"
        @navigate="(stationId) => emit('navigateToStation', stationId)"
      />
    </CollapsibleSection>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import Calendar from '@/assets/icons/calendar.vue'
import Clock from '@/assets/icons/clock.vue'
import CollapsibleSection from './CollapsibleSection.vue'
import StationSourceHistoryAccordion from './StationSourceHistoryAccordion.vue'
import type { SupersededSource } from './StationSourceHistoryAccordion.vue'
import SVGIcon from '@/components/SVGIcon.vue'
import { mdiLockOpenVariant } from '@mdi/js'
import LabelBadge from '@/components/designSystem/Badges/LabelBadge.vue'
import type { LabelBadgeColor } from '@/components/designSystem/Badges/LabelBadge.vue'
import PermanenceLinkRow from '@/components/designSystem/Cards/PermanenceLinkRow.vue'
import { ExceptionSummary } from '@/Network/Types/Requests/DoctorStationExceptionRequests'
import { CheckEvidence } from '@/Network/Types/Requests/StationRequests'
import type {
  StationRegistration,
  StationRegistrationActivityRecord,
  StationRegistrationHistoryEntry,
  StationRegistrationLeg,
  StationRegistrationLegDto
} from '@/Network/Services/StationService'
import type { PermanenceVisibility } from '@/utils/permanence'
import type { DSAttendanceActivityAttachment } from '@/types/ds-attendance.types'
import type {
  StationEvidenceContext,
  StationEvidenceLeg,
  StationSourceOrigin
} from '@/types/station-evidence.types'
import {
  VERIFICATION_BUTTON_LABELS,
  VERIFICATION_COLUMN_HEADER
} from '@/constants/check-verification.constants'
import { getVerificationButtonState } from '@/utils/check-verification'
import {
  DSAttendanceStatus,
  EFFECTIVE_REGISTRATION_STATUS_LABELS,
  EFFECTIVE_SOURCE_KIND_LABELS,
  EffectiveRegistrationStatus,
  EffectiveSourceKind,
  REGISTRATION_NOT_EVALUATED_LABEL
} from '@/constants/ds-attendance.constants'
import {
  formatDateInOffset,
  formatDateTimeWithOffset,
  formatHourMinuteInOffset
} from '@/utils/date'

interface FormattedDateTime {
  weekday: string
  date: string
  time: string
}

/**
 * Resposta de `GET /stations/:doctorStationId/registration`.
 *
 * O contrato mora no `StationService`; estes apelidos existem porque o modal e o
 * spec desta seção já os importam daqui. Os campos além do recorte mínimo
 * (`expected`, `computedAt`, `ruleVersion`, `frozenAt`) são opcionais: o backend
 * da F2 ainda não existe e a tela omite o que não vier.
 */
export type StationRegistrationLegView = StationRegistrationLeg
export type StationRegistrationView = StationRegistration

const props = withDefaults(
  defineProps<{
    exceptionSummary: ExceptionSummary | null
    showDoctorCheck: boolean
    mainCheck: {
      id: number
      originLabel: string
      checkEvidences?: CheckEvidence[]
      checkMethod: string | null
      checkinLatitude: string | number | null
      checkinLongitude: string | number | null
      checkoutLatitude: string | number | null
      checkoutLongitude: string | number | null
    } | null
    permanenceVisibility: PermanenceVisibility
    attendanceStatus: {
      hasApprovedRecord: boolean
      containerId?: number
      status?: string
      isOverdue?: boolean
      sourceApp?: string | null
    }
    loadingDsEvidence: boolean
    hasStationStarted: boolean
    hasStationEnded: boolean
    canReadDoctorCheck: boolean
    isConciliationOrigin: boolean
    checkin: FormattedDateTime | null
    checkout: FormattedDateTime | null
    /** F2 — `null` (backend antigo) mantém o render de hoje, sem chip nem ranking. */
    registration?: StationRegistrationView | null
    /** Mensagem da falha ao carregar o registro; liga o estado de erro + retry. */
    loadError?: string | null
    /** Previsto do plantão (ISO), usado quando a perna não traz o seu. */
    expectedCheckin?: string | null
    expectedCheckout?: string | null
    /** Offset ISO do plantão (`-03:00`). */
    timezone?: string
    /**
     * I9 — o CTA "Enviar registro" desta seção abre o MESMO hub do botão do menu
     * flutuante, e por isso responde ao MESMO gate (permissão de emissão +
     * política da escala). Nasce `false`: sem o gate resolvido lá em cima, o
     * botão não aparece — clique morto é pior do que botão ausente.
     */
    canSendRecord?: boolean
    /**
     * Q1 (spec 2026-09-25 §2.2) — `false` = médico fora do time: o registro não é exigido.
     * `null`/ausente mantém a regra de hoje (chamador que ainda não passa a prop).
     */
    ourTeam?: boolean | null
    /** Alvo de toque de 40px no cabeçalho do acordeão (celular, R6). */
    touchTarget?: boolean
  }>(),
  {
    touchTarget: false,
    registration: null,
    loadError: null,
    expectedCheckin: null,
    expectedCheckout: null,
    timezone: '-03:00',
    canSendRecord: false,
    ourTeam: null
  }
)

const emit = defineEmits<{
  navigateToStation: [stationId: number]
  openEvidence: [files: CheckEvidence[]]
  openDsAttendanceEvidence: []
  openVerification: [checkId: number]
  /** F2 — o gestor pediu o recálculo depois de uma falha. */
  retryRegistration: []
  /** F2 — CTA da perna sem fonte; quem abre o modal de M2 é o Detalhes do Plantão. */
  sendRecord: []
  /**
   * F4 — "Ver evidências": o modal com as abas das duas pernas. Quem o monta é o
   * Detalhes do Plantão, FORA do seu próprio overlay (que é transformado quando
   * uma sidebar abre, e um `position: fixed` descendente nasceria deslocado).
   * O contexto vai pronto daqui porque só esta seção sabe qual fonte venceu cada
   * perna e onde ela mora.
   */
  viewEvidence: [context: StationEvidenceContext]
}>()

// Check-out ausente tem dois significados opostos e o texto precisa distingui-los:
// num plantão que ainda não terminou o registro está por vir ("Aguardando"), num
// plantão já encerrado ele deixou de acontecer e é uma pendência real do gestor.
const missingCheckoutLabel = computed(() =>
  props.hasStationEnded ? 'Não registrado' : 'Aguardando'
)

// Qual botão a coluna "Local e biometria" mostra. A regra vive em
// `getVerificationButtonState` (função pura, testada) porque depende do
// `checkMethod` do backend — não da origem do registro nem dos anexos.
const verificationState = computed(() => getVerificationButtonState(props.mainCheck))

const STATUS_COLORS: Record<EffectiveRegistrationStatus, LabelBadgeColor> = {
  [EffectiveRegistrationStatus.OK]: 'green',
  [EffectiveRegistrationStatus.PARTIAL]: 'orange',
  [EffectiveRegistrationStatus.PENDING]: 'yellow',
  [EffectiveRegistrationStatus.NONE]: 'red',
  [EffectiveRegistrationStatus.NOT_REQUIRED]: 'gray'
}

/**
 * O payload CRU de `GET /stations/:id/registration` (F4). O recorte achatado que
 * o resto do componente lê é uma tradução feita na camada de rede; tudo o que a
 * tela por perna precisa a mais — histórico com veredito, Registros de Atividade
 * da vaga, `frozen.reason`, a fonte como objeto — só existe aqui.
 */
const raw = computed(() => props.registration?.raw ?? null)

const isFrozen = computed(() => Boolean(props.registration?.frozenAt))

const frozenText = computed(() => {
  const reason = raw.value?.frozen?.reason
  if (reason) return reason

  const at = props.registration?.frozenAt
  const when = at ? formatDateTimeWithOffset(at, props.timezone) : null
  return `${when ? `Pós-plantão aprovado em ${when}. ` : ''}O horário efetivo está congelado. Novos registros continuam sendo aceitos e auditados, mas só passam a valer se o pós-plantão for reaberto.`
})

/**
 * "Não avaliado" (M5, balão ⑤): plantão fora da vigência de qualquer política de
 * registro. "Sem registro" ali seria uma acusação — o plantão não deixou de
 * registrar, ele nunca foi avaliado.
 *
 * A F4-FX2 passou a servir `policyInEffect` nesta rota, e é ele que manda: era a
 * SEGUNDA derivação da mesma pergunta (o pós-plantão já lia
 * `registrationPolicyInEffect`) e duas fontes de verdade divergem no primeiro
 * caso de borda. A derivação antiga ("o motor da F1 nunca rodou para esta vaga")
 * fica só como fallback de binário anterior à FX2, onde o campo vem `undefined`.
 */
const isNotEvaluated = computed(() => {
  const payload = raw.value
  if (!payload) return false
  if (payload.policyInEffect !== undefined) return !payload.policyInEffect
  return !payload.computedAt && payload.ruleVersion == null
})

/**
 * "Em andamento" (M1, Tela 3): plantão que ainda não terminou não tem check-out
 * "faltando" — ele está por vir. O chip do topo diz isso em vez de acusar
 * pendência que ainda não existe.
 */
const isOngoing = computed(() => !props.hasStationEnded && !props.registration?.checkout?.value)

/**
 * Q1 — fora do time e sem registro completo: "Não exigido" (neutro). Com registro OK o chip
 * continua dizendo "Registro OK": a verdade das fontes não muda, só deixa de ser cobrança.
 */
const isNotRequired = computed(
  () => props.ourTeam === false && props.registration?.status !== EffectiveRegistrationStatus.OK
)

const statusLabel = computed(() => {
  if (isNotRequired.value)
    return EFFECTIVE_REGISTRATION_STATUS_LABELS[EffectiveRegistrationStatus.NOT_REQUIRED]
  if (isNotEvaluated.value) return REGISTRATION_NOT_EVALUATED_LABEL
  if (isOngoing.value) return 'Em andamento'
  const status = props.registration?.status
  return (
    props.registration?.statusLabel ||
    (status && EFFECTIVE_REGISTRATION_STATUS_LABELS[status]) ||
    'Sem registro'
  )
})

const statusColor = computed<LabelBadgeColor>(() => {
  if (isNotRequired.value) return 'gray'
  if (isNotEvaluated.value) return 'gray'
  if (isOngoing.value) return 'blue'
  const status = props.registration?.status
  return (status && STATUS_COLORS[status]) || 'gray'
})

const LEG_NAMES = { checkin: 'CHECKIN', checkout: 'CHECKOUT' } as const

interface LegView {
  key: 'checkin' | 'checkout'
  title: string
  time: string
  at: string | null
  timeDetail: string
  sourceLabel: string | null
  emptyLabel: string
  rankLabel: string | null
  verified: boolean
  declared: boolean
  origin: StationSourceOrigin | null
  /** Anexos do Registro de Atividade que originou a perna; vazio nas demais fontes. */
  attachments: DSAttendanceActivityAttachment[]
  supersededSources: SupersededSource[]
}

/** `99` = fonte fora do ranking configurado para a escala (F1). */
const rankLabelOf = (rank: number | null | undefined): string | null => {
  if (rank == null) return null
  return rank === 99 ? 'Fora do ranking da escala' : `${rank}º no ranking aplicável`
}

/**
 * Histórico daquela perna, com o veredito de cada fonte. O cru (`history`) é a
 * fonte preferida porque separa `SUPERSEDED` de `DISCARDED`; a lista achatada do
 * normalizador (que marca tudo como superada) é o fallback para backend antigo.
 */
const historyFor = (key: 'checkin' | 'checkout', leg: StationRegistrationLeg | null) => {
  const history = raw.value?.history
  if (!history) return leg?.supersededSources ?? []

  return history
    .filter((entry: StationRegistrationHistoryEntry) => entry.leg === LEG_NAMES[key])
    .map((entry: StationRegistrationHistoryEntry) => ({
      source: entry.kind,
      value: entry.at ?? null,
      rank: entry.rank ?? null,
      outcome: entry.outcome,
      reason: entry.reason ?? null,
      label: entry.label ?? null
    }))
}

const AUDIT_IN_PROGRESS_STATUSES: string[] = [
  DSAttendanceStatus.PENDING_AUDIT,
  DSAttendanceStatus.AUDIT_IN_PROGRESS
]

/**
 * Horário DECLARADO e ainda não final (M1, Tela 3 — estado "Pendente"): existe um
 * Registro de Atividade em auditoria cobrindo a perna. Ele aparece na tela, com o
 * selo "declarado", mas não é o efetivo — e some assim que a perna resolve.
 */
const declaredFor = (key: 'checkin' | 'checkout'): string | null => {
  const records = raw.value?.activityRecords ?? []
  const pending = records.find((record: StationRegistrationActivityRecord) => {
    if (!AUDIT_IN_PROGRESS_STATUSES.includes(String(record.status))) return false
    return key === 'checkin' ? !!record.declaredCheckin : !!record.declaredCheckout
  })
  if (!pending) return null
  return (key === 'checkin' ? pending.declaredCheckin : pending.declaredCheckout) ?? null
}

/** A fonte vencedora como objeto, para o painel de origem. */
const originFor = (
  key: 'checkin' | 'checkout',
  leg: StationRegistrationLeg | null
): StationSourceOrigin | null => {
  const dto = (raw.value?.legs?.[key] ?? raw.value?.[key]) as StationRegistrationLegDto | undefined
  if (dto?.source) return dto.source
  if (!leg?.source) return null
  // Backend antigo: só o `kind` e o rank vieram achatados.
  return { kind: leg.source, rank: leg.sourceRank, refId: null, dsAttendanceContainerId: null }
}

/** Rótulo pt-BR da fonte vencedora; o do backend ganha do mapa local. */
const originLabel = (origin: StationSourceOrigin | null): string | null =>
  origin ? origin.label || EFFECTIVE_SOURCE_KIND_LABELS[origin.kind] : null

/**
 * Check-out ausente tem dois significados opostos: num plantão que ainda não
 * terminou o registro está por vir, num já encerrado ele deixou de acontecer.
 */
const emptyLegLabel = (key: 'checkin' | 'checkout'): string =>
  key === 'checkout' && !props.hasStationEnded ? 'Aguardando' : 'Sem fonte'

/** "12/09/2026 · previsto 07:00" — a linha de contexto abaixo do horário. */
const legTimeDetail = (at: string | null, expectedIso: string | null): string => {
  const date = at ? formatDateInOffset(at, props.timezone) : null
  const expectedTime = expectedIso ? formatHourMinuteInOffset(expectedIso, props.timezone) : null
  return [date, expectedTime ? `previsto ${expectedTime}` : null].filter(Boolean).join(' · ')
}

/**
 * Anexos do Registro de Atividade que venceu a perna. Casamos pelo container da
 * fonte, não pelo primeiro registro da lista: uma vaga pode ter vários, e o que
 * venceu o check-in não é necessariamente o que venceu o check-out.
 */
const attachmentsFor = (origin: StationSourceOrigin | null): DSAttendanceActivityAttachment[] => {
  if (origin?.kind !== EffectiveSourceKind.ACTIVITY_RECORD) return []
  const containerId = origin.dsAttendanceContainerId
  if (containerId == null) return []
  const record = (raw.value?.activityRecords ?? []).find(
    (item: StationRegistrationActivityRecord) => item.containerId === containerId
  )
  return record?.attachments ?? []
}

const buildLegView = (
  key: 'checkin' | 'checkout',
  title: string,
  leg: StationRegistrationLeg | null,
  expected: string | null
): LegView => {
  const declared = leg?.value ? null : declaredFor(key)
  const at = leg?.value ?? declared
  const time = at ? formatHourMinuteInOffset(at, props.timezone) : null
  const origin = originFor(key, leg)

  return {
    key,
    title,
    time: time ?? '--:--',
    at,
    timeDetail: legTimeDetail(at, leg?.expected ?? expected),
    sourceLabel: originLabel(origin),
    emptyLabel: emptyLegLabel(key),
    rankLabel: rankLabelOf(origin?.rank ?? leg?.sourceRank),
    verified: Boolean(leg?.verifiedAt),
    declared: Boolean(declared) || Boolean(leg?.declared),
    origin,
    attachments: attachmentsFor(origin),
    supersededSources: historyFor(key, leg)
  }
}

const legViews = computed<LegView[]>(() => {
  const registration = props.registration
  if (!registration) return []
  return [
    buildLegView('checkin', 'Check-in', registration.checkin, props.expectedCheckin),
    buildLegView('checkout', 'Check-out', registration.checkout, props.expectedCheckout)
  ]
})

const hasLegWithoutSource = computed(() => legViews.value.some((leg) => !leg.sourceLabel))

const hasAnyOrigin = computed(() => legViews.value.some((leg) => Boolean(leg.origin)))

function emitViewEvidence() {
  const legs: StationEvidenceLeg[] = legViews.value.map((leg) => ({
    key: leg.key,
    title: leg.title,
    at: leg.at,
    origin: leg.origin,
    attachments: leg.attachments
  }))
  // Abre na primeira perna COM fonte: abrir num estado vazio faria o gestor achar
  // que não há evidência nenhuma.
  const initialLeg = legs.find((leg) => leg.origin)?.key ?? 'checkin'
  emit('viewEvidence', { legs, initialLeg, timezone: props.timezone })
}

const footerText = computed(() => {
  const registration = props.registration
  if (!registration) return ''

  const parts: string[] = []
  if (registration.computedAt) {
    parts.push(
      `Horário efetivo calculado em ${formatDateTimeWithOffset(
        registration.computedAt,
        props.timezone
      )}`
    )
  }
  if (registration.ruleVersion != null) {
    parts.push(`regra de ranking v${registration.ruleVersion}`)
  }
  if (raw.value?.legacy) {
    parts.push('valor herdado da base anterior ao ranking')
  }
  parts.push('o recálculo não marca o plantão como “alterado após auditoria”')
  return `${parts.join(' · ')}.`
})

function openEvidence(files: CheckEvidence[]) {
  emit('openEvidence', files)
}

function openDsAttendanceEvidence() {
  emit('openDsAttendanceEvidence')
}
</script>

<style scoped>
/* A borda de card envolve a seção inteira (título + corpo). O grid interno e o
 * estado vazio não têm borda própria para não virar card dentro de card. */
.station-registration-card {
  border: 1px solid var(--Neutral-LightGray-30, #d8d8e2);
}

/*
 * Layout fluido da linha de registro: cada bloco tem uma largura mínima e
 * cresce para ocupar a linha; quando não cabe (modal ~720 px de área útil),
 * os blocos de trás quebram para a linha seguinte em vez de espremer as
 * colunas. Antes era um grid de 5 colunas fixas que cortava o rótulo do
 * botão ("BIOMETRIA E M") e empilhava data/hora.
 */
.station-block {
  flex: 1 1 var(--station-block-basis, 150px);
}
.station-block--check {
  --station-block-basis: 250px;
  flex-grow: 2;
}
.station-block--origin {
  --station-block-basis: 160px;
}
.station-block--verification {
  --station-block-basis: 190px;
}
.station-block--evidence {
  --station-block-basis: 140px;
}
/* data e hora dividem a linha do bloco de check e quebram quando falta espaço */
.station-field {
  flex: 1 1 120px;
}
/* a caixa da hora pode carregar o badge "PERMANÊNCIA": quebra o badge para a
 * linha de baixo em vez de vazar por cima do bloco vizinho */
.station-field--time {
  flex-basis: 140px;
}
.station-field--time :deep(.label-badge),
.station-field--time > [data-testid$='-chip'] {
  white-space: nowrap;
  max-width: 100%;
}
/* Botões de ação (verificação e evidência): uma linha, caixa normal, legíveis. */
.station-action-btn {
  height: 32px !important;
  min-height: 0 !important;
  padding: 0 10px !important;
  font-size: 12px !important;
  font-weight: 600;
  letter-spacing: 0;
  text-transform: none;
}
.station-action-btn :deep(.v-btn__content) {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
}
</style>
